import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { notesApi } from '../services/noteApi';
import { NoteWithDetails, NoteSearchParams, PaginatedNotesResponse } from '../types';
import { debounce } from '../../../../utils/validation';
import { useLoadingState } from '../../../../hooks/useLoadingState';

const DEFAULT_LIMIT = 20;
const DEFAULT_SORT = {
  field: 'dateCreated' as const,
  direction: 'desc' as const
};

export const useNotes = () => {
  const [notes, setNotes] = useState<NoteWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    isLoading: globalLoading,
    message: loadingMessage,
    startLoading,
    stopLoading
  } = useLoadingState(250);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 0
  });

  const initialSearchParams: NoteSearchParams = {
    search: '',
    page: 1,
    limit: DEFAULT_LIMIT,
    sorts: [DEFAULT_SORT]
  };

  const [searchParams, setSearchParams] = useState<NoteSearchParams>(initialSearchParams);
  const searchParamsRef = useRef<NoteSearchParams>(initialSearchParams);

  const syncSearchParamsRef = useCallback((params: NoteSearchParams) => {
    searchParamsRef.current = params;
  }, []);

  const fetchNotes = useCallback(async (params?: NoteSearchParams) => {
    const requestParams = params ?? searchParamsRef.current;

    setLoading(true);
    setError(null);
    startLoading('fetch-notes', 'Loading notes...');

    try {
      const response: PaginatedNotesResponse = await notesApi.getAll(requestParams);
      setNotes(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notes');
    } finally {
      setLoading(false);
      stopLoading('fetch-notes');
    }
  }, [startLoading, stopLoading]);

  const debouncedFetchNotes = useMemo(() => debounce(fetchNotes, 300), [fetchNotes]);

  useEffect(() => {
    fetchNotes(initialSearchParams);
  }, [fetchNotes]);

  const updateSearchParams = useCallback((newParams: Partial<NoteSearchParams>) => {
    setSearchParams((prevParams) => {
      const updatedParams: NoteSearchParams = {
        ...prevParams,
        ...newParams
      };

      // Reset to first page when search fields change
      if (newParams.search !== undefined || newParams.fields !== undefined) {
        updatedParams.page = 1;
        debouncedFetchNotes(updatedParams);
      } else if (newParams.sorts !== undefined) {
        // Reset to first page when sorting changes
        updatedParams.page = 1;
        fetchNotes(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        fetchNotes(updatedParams);
      } else {
        fetchNotes(updatedParams);
      }

      syncSearchParamsRef(updatedParams);
      return updatedParams;
    });
  }, [debouncedFetchNotes, fetchNotes, syncSearchParamsRef]);

  const deleteNote = useCallback(async (id: number) => {
    startLoading('delete-note', 'Deleting note...');
    try {
      await notesApi.delete(id);
      await fetchNotes(searchParamsRef.current);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
      return false;
    } finally {
      stopLoading('delete-note');
    }
  }, [fetchNotes, startLoading, stopLoading]);

  const getNoteById = useCallback(async (id: number): Promise<NoteWithDetails | null> => {
    startLoading('fetch-note', 'Loading note details...');
    try {
      const note = await notesApi.getById(id);
      return note;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch note details');
      return null;
    } finally {
      stopLoading('fetch-note');
    }
  }, [startLoading, stopLoading]);

  return {
    notes,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    deleteNote,
    getNoteById,
    refetch: fetchNotes,
    globalLoading,
    loadingMessage
  };
};
